import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Building2, FileText, Landmark, Receipt, Users } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/data";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, NoMoneyAccess, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  monthRange, money, th, td, useCanSeeMoney, useRange, useReport, type ExportSpec,
} from "../ReportKit";

// ─── Types ───────────────────────────────────────────────────────────────────

type Basis = "invoice" | "event";

interface Totals { invoices: number; taxable: number; cgst: number; sgst: number; gst: number; total: number }

interface InvoiceRow {
  id: number;
  number: string;
  date: string | null;
  cancelled: boolean;
  event: { id: number; name: string; start_date: string | null };
  client: { id: number; name: string; gstin: string | null } | null;
  type: "B2B" | "B2C";
  taxable: number;
  rate: number;
  cgst: number;
  sgst: number;
  gst: number;
  total: number;
}

interface GstReport {
  period: { from: string; to: string };
  basis: Basis;
  business_gstin: string | null;
  summary: Totals & { b2b: Totals; b2c: Totals; no_gst: Totals; gst_in_payments: number };
  by_rate: (Totals & { rate: number })[];
  by_month: (Totals & { month: string; month_name: string })[];
  invoices: InvoiceRow[];
  cancelled: InvoiceRow[];
}

type Filter = "all" | "B2B" | "B2C" | "none";

const rateLabel = (r: number) => (r > 0 ? `${r}%` : "No GST");

// ─── Page ────────────────────────────────────────────────────────────────────

export default function GstReportPage() {
  const canSeeMoney = useCanSeeMoney();
  // GST is filed for the month just gone, so start there
  const range = useRange(monthRange(-1));
  const { from, to } = range.applied;
  const [basis, setBasis] = useState<Basis>("invoice");

  const state = useReport<GstReport>(
    async () => (await reportsApi.gst({ from_date: from, to_date: to, basis })).data,
    [from, to, basis],
    canSeeMoney,
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={Landmark}
          iconClass="text-violet-600"
          title="GST Report"
          subtitle="Invoices with GST, ready for your accountant"
        >
          {canSeeMoney && (
            <HeaderTools>
              <div className="inline-flex h-10 rounded-xl border border-stone-200 bg-stone-50 p-0.5 shrink-0" role="radiogroup" aria-label="Count invoices by">
                {(["invoice", "event"] as const).map((b) => (
                  <button
                    key={b}
                    type="button"
                    role="radio"
                    aria-checked={basis === b}
                    onClick={() => setBasis(b)}
                    className={`flex-1 px-3 rounded-lg text-sm font-medium whitespace-nowrap ${basis === b ? "bg-white shadow-sm text-stone-800" : "text-stone-500"}`}
                  >
                    {b === "invoice" ? "Invoice date" : "Event date"}
                  </button>
                ))}
              </div>
              <RangeControls range={range} />
              <ExportButtons build={() => state.data && buildExport(state.data)} disabled={state.loading || !state.data} />
            </HeaderTools>
          )}
        </ReportHeader>

        {!canSeeMoney ? <NoMoneyAccess what="the GST report" /> : (
          <ReportBody state={state}>
            {(d) => <GstView d={d} />}
          </ReportBody>
        )}
      </div>
    </DashboardLayout>
  );
}

function GstView({ d }: { d: GstReport }) {
  const s = d.summary;
  const [filter, setFilter] = useState<Filter>("all");

  const rows = useMemo(() => d.invoices.filter((r) =>
    filter === "all" ? true : filter === "none" ? r.gst <= 0 : r.type === filter,
  ), [d.invoices, filter]);
  const shown = rows.reduce(
    (a, r) => ({ taxable: a.taxable + r.taxable, cgst: a.cgst + r.cgst, sgst: a.sgst + r.sgst, gst: a.gst + r.gst, total: a.total + r.total }),
    { taxable: 0, cgst: 0, sgst: 0, gst: 0, total: 0 },
  );

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: "all", label: "All", count: s.invoices },
    { key: "B2B", label: "With GSTIN", count: s.b2b.invoices },
    { key: "B2C", label: "Without GSTIN", count: s.b2c.invoices },
    { key: "none", label: "No GST", count: s.no_gst.invoices },
  ];

  return (
    <>
      {/* Our own GSTIN goes on every invoice; point to Settings if it's missing */}
      {d.business_gstin ? (
        <p className="text-xs text-stone-500">Your GSTIN: <span className="font-semibold text-stone-700 tracking-wide">{d.business_gstin}</span></p>
      ) : (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Your business GSTIN isn’t saved yet.{" "}
            <Link to="/settings?tab=business" className="font-semibold underline">Add it in Settings</Link> so it appears on invoices and this report.
          </p>
        </div>
      )}

      <KpiGrid>
        <Kpi label="Value before GST" icon={FileText} tone="stone" value={money(s.taxable)} sub={`${s.invoices} invoice${s.invoices === 1 ? "" : "s"}`} />
        <Kpi label="GST on invoices" icon={Landmark} tone="violet" value={money(s.gst)} sub={`CGST ${money(s.cgst)} · SGST ${money(s.sgst)}`} />
        <Kpi label="With client GSTIN" icon={Building2} tone="blue" value={money(s.b2b.gst)} sub={`GST on ${s.b2b.invoices} business invoice${s.b2b.invoices === 1 ? "" : "s"} (B2B)`} />
        <Kpi label="Without GSTIN" icon={Users} tone="amber" value={money(s.b2c.gst)} sub={`GST on ${s.b2c.invoices} invoice${s.b2c.invoices === 1 ? "" : "s"} to individuals (B2C)`} />
      </KpiGrid>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="By GST rate">
          {d.by_rate.length === 0 ? <Empty /> : (
            <TableScroll minWidth={480}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className={`${th} text-left`}>Rate</th>
                    <th className={`${th} text-right`}>Invoices</th>
                    <th className={`${th} text-right`}>Value before GST</th>
                    <th className={`${th} text-right`}>CGST</th>
                    <th className={`${th} text-right`}>SGST</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {d.by_rate.map((r) => (
                    <tr key={r.rate}>
                      <td className={`${td} font-medium text-stone-700`}>{rateLabel(r.rate)}</td>
                      <td className={`${td} text-right text-stone-600`}>{r.invoices}</td>
                      <td className={`${td} text-right tabular-nums`}>{money(r.taxable)}</td>
                      <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.cgst)}</td>
                      <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.sgst)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                    <td className={td}>Total</td>
                    <td className={`${td} text-right`}>{s.invoices}</td>
                    <td className={`${td} text-right tabular-nums`}>{money(s.taxable)}</td>
                    <td className={`${td} text-right tabular-nums`}>{money(s.cgst)}</td>
                    <td className={`${td} text-right tabular-nums`}>{money(s.sgst)}</td>
                  </tr>
                </tfoot>
              </table>
            </TableScroll>
          )}
        </Card>

        <Card title={d.by_month.length > 1 ? "By month" : "Money received"} subtitle={d.by_month.length > 1 ? undefined : "For reference"}>
          {d.by_month.length > 1 ? (
            <ul className="divide-y divide-stone-100 text-sm">
              {d.by_month.map((m) => (
                <li key={m.month} className="py-2 flex items-center justify-between gap-3">
                  <span className="font-medium text-stone-700">{m.month_name}</span>
                  <span className="text-right">
                    <span className="block tabular-nums font-semibold text-stone-800">{money(m.gst)} GST</span>
                    <span className="block text-xs text-stone-500 tabular-nums">{m.invoices} invoice{m.invoices === 1 ? "" : "s"} · {money(m.taxable)} before GST</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <p className={`text-sm text-stone-600 ${d.by_month.length > 1 ? "mt-3 pt-3 border-t border-stone-100" : ""}`}>
            GST inside payments received in these dates: <span className="font-semibold tabular-nums text-stone-800">{money(s.gst_in_payments)}</span>
          </p>
          <Note>Useful if your accountant pays GST on advances when they are received.</Note>
        </Card>
      </div>

      <Card
        title="Invoices"
        subtitle={d.basis === "invoice" ? "By the date each invoice was issued" : "By the date of each event"}
        actions={
          <div className="flex flex-wrap gap-1.5">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`h-9 px-3 rounded-full border text-xs font-medium ${filter === f.key ? "border-amber-400 bg-amber-50 text-amber-800" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
        }
      >
        {rows.length === 0 ? <Empty /> : (
          <>
            {/* Phones: a card per invoice */}
            <div className="md:hidden divide-y divide-stone-100">
              {rows.map((r) => (
                <div key={r.id} className="py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-stone-800">{r.number} <span className="font-normal text-xs text-stone-500">· {r.date ? formatDate(r.date) : "—"}</span></p>
                      <p className="text-sm text-stone-700 truncate">{r.client?.name ?? "No client"}</p>
                      <p className="text-xs text-stone-500 truncate">{r.client?.gstin ? `GSTIN ${r.client.gstin}` : "No GSTIN"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold tabular-nums text-stone-800">{money(r.gst)}</p>
                      <p className="text-[11px] text-stone-500">{rateLabel(r.rate)} on {money(r.taxable)}</p>
                    </div>
                  </div>
                  <Link to={`/events/detail?id=${r.event.id}`} className="text-xs text-stone-500 hover:text-amber-700 mt-1 inline-block">
                    {r.event.name}{r.event.start_date ? ` · ${formatDate(r.event.start_date)}` : ""}
                  </Link>
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              <TableScroll minWidth={960}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 text-left">
                      <th className={th}>Date</th>
                      <th className={th}>Invoice</th>
                      <th className={th}>Client</th>
                      <th className={th}>GSTIN</th>
                      <th className={`${th} text-right`}>Value before GST</th>
                      <th className={`${th} text-right`}>Rate</th>
                      <th className={`${th} text-right`}>CGST</th>
                      <th className={`${th} text-right`}>SGST</th>
                      <th className={`${th} text-right`}>Invoice total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-stone-50/60">
                        <td className={`${td} text-stone-600`}>{r.date ? formatDate(r.date) : "—"}</td>
                        <td className={td}>
                          <p className="font-medium text-stone-800">{r.number}</p>
                          <Link to={`/events/detail?id=${r.event.id}`} className="text-xs text-stone-500 hover:text-amber-700">{r.event.name}</Link>
                        </td>
                        <td className={`${td} text-stone-700`}>{r.client?.name ?? "No client"}</td>
                        <td className={`${td} text-stone-600 font-mono text-xs`}>{r.client?.gstin ?? <span className="font-sans text-stone-400">—</span>}</td>
                        <td className={`${td} text-right tabular-nums`}>{money(r.taxable)}</td>
                        <td className={`${td} text-right text-stone-600`}>{rateLabel(r.rate)}</td>
                        <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.cgst)}</td>
                        <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.sgst)}</td>
                        <td className={`${td} text-right tabular-nums font-semibold text-stone-800`}>{money(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                      <td className={td} colSpan={4}>Total ({rows.length})</td>
                      <td className={`${td} text-right tabular-nums`}>{money(shown.taxable)}</td>
                      <td className={td} />
                      <td className={`${td} text-right tabular-nums`}>{money(shown.cgst)}</td>
                      <td className={`${td} text-right tabular-nums`}>{money(shown.sgst)}</td>
                      <td className={`${td} text-right tabular-nums`}>{money(shown.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </TableScroll>
            </div>
          </>
        )}
      </Card>

      {d.cancelled.length > 0 && (
        <Card title="Cancelled invoices (not counted)" subtitle="Your accountant may need these numbers for credit notes">
          <ul className="divide-y divide-stone-100 text-sm">
            {d.cancelled.map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-stone-700">{r.number} <span className="text-xs font-normal text-stone-500">· {r.date ? formatDate(r.date) : "—"}</span></p>
                  <p className="text-xs text-stone-500 truncate">{r.client?.name ?? "No client"} · {r.event.name}</p>
                </div>
                <span className="text-right shrink-0">
                  <span className="block tabular-nums text-stone-700">{money(r.total)}</span>
                  <span className="block text-[11px] text-stone-500 tabular-nums">GST {money(r.gst)}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Note>
        CGST and SGST are shown as half each, which is right for clients in your own state. For a client from another state it
        should be IGST instead — check those with your accountant. Draft events are left out. Invoice date is the day the booking
        was made; switch to “Event date” if your accountant counts GST when the event happens.
      </Note>
    </>
  );
}

function Empty() {
  return (
    <div className="py-8 flex flex-col items-center gap-2 text-center">
      <Receipt className="w-6 h-6 text-stone-300" />
      <p className="text-sm text-stone-400">No invoices in these dates</p>
    </div>
  );
}

// ─── Excel / Print ───────────────────────────────────────────────────────────

function buildExport(d: GstReport): ExportSpec {
  const s = d.summary;
  const invoiceColumns = [
    { label: "Invoice date", type: "date" as const }, { label: "Invoice" }, { label: "Client" }, { label: "Client GSTIN" },
    { label: "B2B / B2C" }, { label: "Event" }, { label: "Event date", type: "date" as const },
    { label: "Value before GST", type: "money" as const }, { label: "GST rate", type: "percent" as const },
    { label: "CGST", type: "money" as const }, { label: "SGST", type: "money" as const },
    { label: "Total GST", type: "money" as const }, { label: "Invoice total", type: "money" as const },
  ];
  const invoiceRow = (r: InvoiceRow) => [
    r.date, r.number, r.client?.name ?? "", r.client?.gstin ?? "", r.type, r.event.name, r.event.start_date,
    r.taxable, r.rate, r.cgst, r.sgst, r.gst, r.total,
  ];
  return {
    title: "GST Report",
    fileName: "gst-report",
    period: d.period,
    summary: [
      { label: "Our GSTIN", value: d.business_gstin ?? "Not saved" },
      { label: "Counted by", value: d.basis === "invoice" ? "Invoice date" : "Event date" },
      { label: "Invoices", value: s.invoices, type: "number" },
      { label: "Value before GST", value: s.taxable, type: "money" },
      { label: "CGST", value: s.cgst, type: "money" },
      { label: "SGST", value: s.sgst, type: "money" },
      { label: "Total GST", value: s.gst, type: "money" },
      { label: "Invoice total (incl. GST)", value: s.total, type: "money" },
      { label: "B2B (with GSTIN): value before GST", value: s.b2b.taxable, type: "money" },
      { label: "B2B (with GSTIN): GST", value: s.b2b.gst, type: "money" },
      { label: "B2C (without GSTIN): value before GST", value: s.b2c.taxable, type: "money" },
      { label: "B2C (without GSTIN): GST", value: s.b2c.gst, type: "money" },
      { label: "GST inside payments received (for reference)", value: s.gst_in_payments, type: "money" },
    ],
    tables: [
      {
        title: "Invoices",
        columns: invoiceColumns,
        rows: d.invoices.map(invoiceRow),
        totals: ["Total", "", "", "", "", "", "", s.taxable, null, s.cgst, s.sgst, s.gst, s.total],
      },
      {
        title: "By rate",
        columns: [
          { label: "GST rate" }, { label: "Invoices", type: "number" }, { label: "Value before GST", type: "money" },
          { label: "CGST", type: "money" }, { label: "SGST", type: "money" }, { label: "Total GST", type: "money" },
        ],
        rows: d.by_rate.map((r) => [rateLabel(r.rate), r.invoices, r.taxable, r.cgst, r.sgst, r.gst]),
        totals: ["Total", s.invoices, s.taxable, s.cgst, s.sgst, s.gst],
      },
      ...(d.by_month.length > 1 ? [{
        title: "By month",
        columns: [
          { label: "Month" }, { label: "Invoices", type: "number" as const }, { label: "Value before GST", type: "money" as const },
          { label: "CGST", type: "money" as const }, { label: "SGST", type: "money" as const }, { label: "Total GST", type: "money" as const },
        ],
        rows: d.by_month.map((m) => [m.month_name, m.invoices, m.taxable, m.cgst, m.sgst, m.gst]),
        totals: ["Total", s.invoices, s.taxable, s.cgst, s.sgst, s.gst],
      }] : []),
      ...(d.cancelled.length ? [{ title: "Cancelled invoices", columns: invoiceColumns, rows: d.cancelled.map(invoiceRow) }] : []),
    ],
    notes: [
      "CGST and SGST are half each (client in the same state). A client from another state should be IGST instead.",
      "Draft events are left out. Cancelled invoices are listed separately and not counted.",
    ],
  };
}
